import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Calendar } from "@/components/ui/calendar";
import { Users, Clock, MapPin, ArrowRight, Check, Info, Shield } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SpaceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [space, setSpace] = useState(null);
  const [layouts, setLayouts] = useState([]);
  const [userPlan, setUserPlan] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLayout, setSelectedLayout] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/spaces/${id}`),
      axios.get(`${API}/spaces/${id}/layouts`)
    ]).then(([spaceRes, layoutsRes]) => {
      setSpace(spaceRes.data);
      setLayouts(layoutsRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
    if (token) {
      axios.get(`${API}/my-plan`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setUserPlan(r.data))
        .catch(() => {});
    }
  }, [id, token]);

  useEffect(() => {
    if (id && selectedDate) {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      axios.get(`${API}/spaces/${id}/availability?date=${dateStr}`)
        .then(r => setAvailability(r.data)).catch(() => {});
    }
  }, [id, selectedDate]);

  useEffect(() => {
    document.title = space ? `${space.name} - Book Now | The Rig` : "Space Details | The Rig";
  }, [space]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-12"><div className="h-96 bg-muted animate-pulse rounded-xl" /></div>;
  if (!space) return <div className="max-w-7xl mx-auto px-6 py-12 text-center"><p>Space not found</p></div>;

  const generateTimeSlots = () => {
    if (!availability) return [];
    const oh = availability.opening_hours || { start: "08:00", end: "22:00" };
    const startH = parseInt(oh.start.split(":")[0]);
    const endH = parseInt(oh.end.split(":")[0]);
    const slots = [];
    for (let h = startH; h < endH; h++) {
      for (let m = 0; m < 60; m += 30) {
        const time = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
        const fullTime = `${format(selectedDate, "yyyy-MM-dd")}T${time}:00`;
        const isBooked = availability.bookings?.some(b => b.start <= fullTime && b.end > fullTime);
        const isBlocked = availability.blocks?.some(b => b.start <= fullTime && b.end > fullTime);
        slots.push({ time, isBooked, isBlocked });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 animate-fade-in">
      <title>{space?.name ? `${space.name} - Book Now | The Rig` : "Space Details | The Rig"}</title>
      <meta name="description" content={space?.name ? `Book ${space.name} at The Rig. ${space.description?.substring(0, 120) || "Flexible meeting room with multiple layout options."} Capacity: ${space.capacity} people.` : "View space details and book at The Rig coworking."} />
      <meta property="og:title" content={space?.name ? `${space.name} - Book Now | The Rig` : "Space Details | The Rig"} />
      <meta property="og:description" content={space?.description?.substring(0, 160) || "Flexible meeting room with multiple layout options at The Rig coworking space."} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={space?.photos?.[0] || "https://customer-assets.emergentagent.com/job_book-spaces-2/artifacts/2dhwnmm0_therig.png"} />
      {/* Hero Image */}
      <div className="relative overflow-hidden mb-8">
        <img
          src={space.photos?.[0] || "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&h=500&fit=crop"}
          alt={space.name}
          className="w-full h-[350px] object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute bottom-6 left-6">
          <span className="text-white/60 text-xs uppercase tracking-[0.2em]">
            {space.type === "meeting_room" ? "Meeting Room" : "Office"}
          </span>
          <h1 className="font-heading font-bold text-4xl text-white mt-1" data-testid="space-detail-title">{space.name}</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Info */}
        <div className="lg:col-span-2 space-y-8">
          <div>
            <h2 className="font-heading font-semibold text-xl mb-3">About this space</h2>
            <p className="text-muted-foreground" data-testid="space-description">{space.description}</p>
          </div>

          <div>
            <h2 className="font-heading font-semibold text-xl mb-3">Details</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-2 text-sm"><Users className="h-4 w-4 text-primary" />Capacity: {space.capacity}</div>
              <div className="flex items-center gap-2 text-sm"><Clock className="h-4 w-4 text-primary" />{space.opening_hours?.start} - {space.opening_hours?.end}</div>
              <div className="flex items-center gap-2 text-sm"><MapPin className="h-4 w-4 text-primary" />{space.guest_allowed ? "Guests Welcome" : "Members Only"}</div>
            </div>
          </div>

          <div>
            <h2 className="font-heading font-semibold text-xl mb-3">Amenities</h2>
            <div className="flex flex-wrap gap-2" data-testid="space-amenities">
              {space.amenities?.map((a, i) => (
                <Badge key={i} variant="secondary" className="bg-muted text-foreground"><Check className="h-3 w-3 mr-1" />{a}</Badge>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading font-semibold text-xl mb-3">Available Layouts</h2>
            <p className="text-sm text-muted-foreground mb-3">Click a layout to see setup details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="space-layouts">
              {layouts.map((l, i) => (
                <Card
                  key={l.id}
                  className="p-4 border border-border hover:border-primary/30 transition-colors cursor-pointer group"
                  onClick={() => setSelectedLayout(l)}
                  data-testid={`layout-card-${i}`}
                >
                  <div className="flex items-start justify-between">
                    <h3 className="font-medium text-sm">{l.layout_name}</h3>
                    <Info className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{l.layout_description}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    <span><Users className="h-3 w-3 inline mr-1" />Capacity: {l.capacity}</span>
                    {l.setup_buffer_minutes > 0 && <span><Clock className="h-3 w-3 inline mr-1" />{l.setup_buffer_minutes}min setup</span>}
                  </div>
                </Card>
              ))}
            </div>

            {/* Layout Detail Dialog */}
            <Dialog open={!!selectedLayout} onOpenChange={() => setSelectedLayout(null)}>
              <DialogContent className="sm:max-w-lg" data-testid="layout-detail-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">{selectedLayout?.layout_name}</DialogTitle>
                  <DialogDescription>Layout setup instructions for {space?.name}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div>
                    <h4 className="text-sm font-medium mb-1">Description</h4>
                    <p className="text-sm text-muted-foreground">{selectedLayout?.layout_description}</p>
                  </div>
                  <Separator />
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Capacity:</span>
                      <span className="ml-2 font-medium">{selectedLayout?.capacity} people</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Setup Time:</span>
                      <span className="ml-2 font-medium">{selectedLayout?.setup_buffer_minutes || 0} minutes</span>
                    </div>
                  </div>
                  {selectedLayout?.notes && (
                    <>
                      <Separator />
                      <div>
                        <h4 className="text-sm font-medium mb-1">Setup Notes</h4>
                        <p className="text-sm text-muted-foreground">{selectedLayout.notes}</p>
                      </div>
                    </>
                  )}
                  <Separator />
                  <div className="bg-muted/50 rounded p-4 text-sm">
                    <h4 className="font-medium mb-2">Preparation Checklist</h4>
                    <ul className="space-y-1 text-muted-foreground">
                      {selectedLayout?.layout_name === "Boardroom" && (
                        <>
                          <li className="flex items-start gap-2"><Check className="h-3 w-3 mt-1 text-primary flex-shrink-0" />Arrange chairs around central table facing each other</li>
                          <li className="flex items-start gap-2"><Check className="h-3 w-3 mt-1 text-primary flex-shrink-0" />Set up presentation screen at the head of table</li>
                          <li className="flex items-start gap-2"><Check className="h-3 w-3 mt-1 text-primary flex-shrink-0" />Place notepads and water at each seat</li>
                        </>
                      )}
                      {selectedLayout?.layout_name === "Classroom" && (
                        <>
                          <li className="flex items-start gap-2"><Check className="h-3 w-3 mt-1 text-primary flex-shrink-0" />Arrange desks in rows facing the front</li>
                          <li className="flex items-start gap-2"><Check className="h-3 w-3 mt-1 text-primary flex-shrink-0" />Set up projector/screen at the front of the room</li>
                          <li className="flex items-start gap-2"><Check className="h-3 w-3 mt-1 text-primary flex-shrink-0" />Ensure whiteboard markers and eraser are available</li>
                        </>
                      )}
                      {selectedLayout?.layout_name === "Theater" && (
                        <>
                          <li className="flex items-start gap-2"><Check className="h-3 w-3 mt-1 text-primary flex-shrink-0" />Remove all desks, arrange chairs in rows facing the stage area</li>
                          <li className="flex items-start gap-2"><Check className="h-3 w-3 mt-1 text-primary flex-shrink-0" />Test sound system and microphones</li>
                          <li className="flex items-start gap-2"><Check className="h-3 w-3 mt-1 text-primary flex-shrink-0" />Set up podium or speaker stand at front</li>
                        </>
                      )}
                      {selectedLayout?.layout_name === "U-Shape" && (
                        <>
                          <li className="flex items-start gap-2"><Check className="h-3 w-3 mt-1 text-primary flex-shrink-0" />Arrange tables in a U-shape with open end facing the screen</li>
                          <li className="flex items-start gap-2"><Check className="h-3 w-3 mt-1 text-primary flex-shrink-0" />Place chairs along outer edge of U only</li>
                          <li className="flex items-start gap-2"><Check className="h-3 w-3 mt-1 text-primary flex-shrink-0" />Ensure all participants have line of sight to presentation area</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Availability */}
          <div>
            <h2 className="font-heading font-semibold text-xl mb-3">Availability</h2>
            <div className="flex flex-col sm:flex-row gap-6">
              <div data-testid="availability-calendar">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(d) => d && setSelectedDate(d)}
                  disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                  className="rounded-lg border shadow-sm"
                />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium mb-3">Time slots for {format(selectedDate, "MMM d, yyyy")}</p>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="time-slots">
                  {timeSlots.map((s, i) => (
                    <div
                      key={i}
                      className={`time-slot text-center text-xs ${s.isBooked || s.isBlocked ? 'time-slot-unavailable' : 'time-slot-available'}`}
                      data-testid={`time-slot-${s.time}`}
                    >
                      {s.time}
                    </div>
                  ))}
                </div>
                <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><div className="h-3 w-3 rounded-sm bg-muted" />Available</span>
                  <span className="flex items-center gap-1"><div className="h-3 w-3 rounded-sm bg-destructive/20" />Booked</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Booking CTA */}
        <div className="lg:col-span-1">
          <Card className="sticky top-24 p-6 border border-border rounded-2xl shadow-lg" data-testid="booking-cta-card">
            {userPlan && (
              <div className="flex items-center gap-2 mb-4 p-3 bg-rig-teal/5 rounded-lg border border-rig-teal/20" data-testid="plan-badge-detail">
                <Shield className="h-4 w-4 text-rig-teal" />
                <span className="text-sm font-semibold text-rig-charcoal">{userPlan.name} Plan</span>
                {userPlan.hide_prices && <Badge className="bg-rig-teal/10 text-rig-teal text-xs ml-auto">Included</Badge>}
              </div>
            )}
            <h3 className="font-heading font-bold text-xl mb-4 text-rig-charcoal">Pricing</h3>
            {space.type === "office" ? (
              <div className="py-4 text-center mb-6 bg-rig-orange/5 rounded-xl border border-rig-orange/20 px-4">
                <p className="text-rig-charcoal font-semibold text-base mb-1">Contact us for pricing</p>
                <p className="text-xs text-muted-foreground mb-3">Office rates are tailored to your needs</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full border-rig-orange text-rig-orange hover:bg-rig-orange hover:text-white"
                  onClick={() => navigate("/contact")}
                >
                  Get in Touch
                </Button>
              </div>
            ) : userPlan?.hide_prices ? (
              <div className="py-4 text-center mb-6">
                <p className="text-rig-green font-bold text-lg">Included in your plan</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {userPlan.is_unlimited ? "Unlimited hours" : `${Math.max(0, (userPlan.included_hours || 0) - (userPlan.hours_used || 0)).toFixed(1)}h remaining`}
                </p>
              </div>
            ) : (
              <div className="space-y-3 mb-6">
                <div className="flex justify-between"><span className="text-rig-charcoal/50">Hourly</span><span className="font-heading font-bold text-rig-orange">XCG {space.pricing?.hourly}<span className="text-sm font-normal text-rig-charcoal/40">/hr</span></span></div>
                <Separator />
                <div className="flex justify-between"><span className="text-rig-charcoal/50">Daily</span><span className="font-heading font-bold text-rig-green">XCG {space.pricing?.daily}<span className="text-sm font-normal text-rig-charcoal/40">/day</span></span></div>
                <Separator />
                <div className="flex justify-between"><span className="text-rig-charcoal/50">Monthly</span><span className="font-heading font-bold text-rig-teal">XCG {space.pricing?.monthly}<span className="text-sm font-normal text-rig-charcoal/40">/mo</span></span></div>
              </div>
            )}
            <Button
              className="w-full bg-rig-orange text-white hover:bg-rig-orange/90 rounded-full font-bold shadow-lg shadow-rig-orange/25 transition-all active:scale-95"
              onClick={() => navigate(`/book/${space.id}`)}
              data-testid="book-now-btn"
            >
              Book This Space <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <p className="text-xs text-rig-charcoal/40 text-center mt-3">Free cancellation up to 12 hours before</p>
          </Card>
        </div>
      </div>
    </div>
  );
}
